import FieldMetaData = namaTypes.FieldMetaData;
import PrintBarcodeData = namaTypes.PrintBarcodeData;
import SingleBarCodePrint = namaTypes.SingleBarCodePrint;
import MultiBarCodePrintData = namaTypes.MultiBarCodePrintData;
import ClientCache from "@/utils/login/ClientCache.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import EntitiesWithLedgerEffectsOrWithParents from "@/utils/constants/EntitiesWithLedgerEffectsOrWithParents.ts";
import NamaPrinting from "@/utils/common/NamaPrinting.ts";
import {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import {copyToClipboard, Dialog} from "quasar";
import TextToCopyDialog from "@/components/dialogs/TextToCopyDialog.vue";
import GridSize from "@/utils/constants/GridSize.ts";
import Translator from "@/utils/common/Translator.ts";
import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon.ts";
import {NamaIconList} from "@/components/icons/NamaIconList.ts";
import ShortCutsUtil from "@/utils/common/ShortCutsUtil.ts";
import TopPanelSearchUtils from "@/utils/common/TopPanelSearchUtils.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import {RecentVisit, RecentVisitGroupedByPeriod} from "@/utils/presenters/interfaces.ts";
import {RouteLocationNamedRaw} from "vue-router";
import RouteNames from "@/router/RouteNames.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";

export default class GUIUtilities {
  static calcToolbarBtnTitle(businessFunc: string): string {
    let title = Translator.translateId(businessFunc, 'BusinessFunction')!;
    let shortCut = ShortCutsUtil.getFunctionShortCut(businessFunc);
    if (shortCut) {
      title += ShortCutsUtil.describe(shortCut);
    }
    return title;
  }

  static getAllowedValues(entityType: string | undefined | null, fieldMetaData: FieldMetaData): string[] {
    if (fieldMetaData.enumTypeName === "BusinessFunction") {
      return [...fieldMetaData.allowedValues ?? [], ...ClientCache.getAllGUIActions()];
    } else if (fieldMetaData.allowedValues && fieldMetaData.allowedValues.length) {
      if (fieldMetaData.allowedValues.length === 1 && ObjectChecker.isAnyEqualToFirst(fieldMetaData.allowedValues[0], CommonConstants.GENERIC_REF_ALL,
        CommonConstants.GENERIC_REF_SIMPLE_AND_ALL, CommonConstants.GENERIC_REF_ALL_DOCS,
        CommonConstants.GENERIC_REF_ALL_MASTERS))
        return ClientCache.calcAllowedValuesForEntityType(fieldMetaData);
      if (entityType && fieldMetaData.fieldId && ClientCache.doesFieldHaveAllowedValues(entityType, fieldMetaData.fieldId))
        return ClientCache.getAllowedValuesFor(entityType, fieldMetaData.fieldId);
      return fieldMetaData.allowedValues;
    } else if (fieldMetaData.enumTypeName === "MasterEntityTypeDF") {
      return ClientCache.getMasterFileEntityTypes();
    } else if (fieldMetaData.enumTypeName === "DocumentEntityTypeDF") {
      return ClientCache.getDocumentFileEntityTypes();
    } else if (fieldMetaData.enumTypeName === "EntityTypeDF") {
      return ClientCache.getAllEntityTypes();
    } else if (fieldMetaData.fieldType === FieldType.FeatureID) {
      return ClientCache.getAllFeatures();
    }
    return [];
  }

  static async printBarcode(entityType: string, ids: string[]) {
    let line: string = this.idsToBarcodeLine(ids);

    if (!LoginUtils.loginInfo?.alwaysPrint1InBarcode) {
      // TODO:: Create NaMaDialogBox
      let integerBoxValue: number = 1;
      // TODO:: await OnClick OK
      this.printBarcodes(entityType, line, integerBoxValue);
    } else {
      this.printBarcodes(entityType, line, 1);
    }
  }

  static idsToBarcodeLine(ids: string[]): string {
    return StringUtils.toCSVLineWithSep(CommonConstants.HOPEFULLY_UNIQUE_FIELD_SEP, ids);
  }

  static printBarcodes(entityType: string, line: string, labelsCount: number) {
    //TODO:: FrameElement
    let url: string = this.createBarcodeURL(entityType, line, labelsCount);
    // frame.src(url);
  }

  static printBarcodeAction(printBarCode: PrintBarcodeData | undefined) {
    if (!printBarCode || !printBarCode.barCodePrints)
      return;
    let barcodes: MultiBarCodePrintData[] = [];
    for (let print of printBarCode.barCodePrints) {
      if (!print.id)
        continue;
      this.getOrAddBarcodeList(print, barcodes).push(print.id);
    }
    let barcodeURLs: string[] = [];
    for (let entry of barcodes) {
      barcodeURLs.push(this.createBarcodeURL(entry.entityType!, this.idsToBarcodeLine(entry.barCodesIds!), entry.count!));
    }
    NamaPrinting.printNext(barcodeURLs, 0);
  }

  static internalGridSizeToPixels(size: string | undefined | null): number {
    if (!size)
      return 400;
    switch (size) {
      case GridSize.XXLarge: return 900;
      case GridSize.XLarge:  return 700;
      case GridSize.Large:   return 500;
      case GridSize.Medium:  return 300;
      case GridSize.Small:   return 200;
      case GridSize.VerySmall: return 150;
      case GridSize.Tiny:    return 100;
      default: return 400;
    }
  }

  static updateWindowTitle(subtitle: string) {
    let userName: string = "";
    if (LoginUtils.isCurrentLogInInfoValid())
      userName = " - " + Translator.translateRef(LoginUtils.loginInfo?.userInfo);
    document.title = Translator.translateId('applicationName') + " - " + subtitle + userName;
  }

  static getOrAddBarcodeList(print: SingleBarCodePrint, barcodes: MultiBarCodePrintData[]): string[] {
    let entityType = print.entityType ?? '';
    let count = print.count ?? 0;
    let key: string = entityType + count;
    let triple: MultiBarCodePrintData = barcodes[key];
    if (!triple) {
      barcodes[key] = {entityType: entityType, count: count, barCodesIds: []}
    }
    return triple.barCodesIds ?? [];
  }

  static createBarcodeURL(entityType: string, line: string, labelsCount: number) {
    let url: string = CommonConstants.BARCODE_URL_PREFIX + entityType + CommonConstants.HOPEFULLY_UNIQUE_FIELD_SEP + labelsCount
      + CommonConstants.HOPEFULLY_UNIQUE_FIELD_SEP + line + CommonConstants.HOPEFULLY_UNIQUE_FIELD_SEP;
    return url;
  }

  static isEntityWithAccountingEffect(entityType: string) {
    return EntitiesWithLedgerEffectsOrWithParents.entitiesWithLedgerEffects.contains(entityType)
      || EntitiesWithLedgerEffectsOrWithParents.scCostDocs.contains(entityType)
      || EntitiesWithLedgerEffectsOrWithParents.scLedgerDocs.contains(entityType);
  }

  static createGenericRefEntityTypeMeta(typeId: string, metadata: FieldMetaData) {
    let typeMetaData = {} as FieldMetaData;
    Object.assign(typeMetaData, metadata);
    typeMetaData.fieldId = typeId;
    typeMetaData.allowedValues = ClientCache.calcAllowedValuesForEntityType(metadata);
    return typeMetaData;
  }

  static showTextToCopy(textToCopy: string | undefined) {
    let copyBtn: CommonDialogAction = new CommonDialogAction('copy', async () => {
      await copyToClipboard(textToCopy || "");
    });

    Dialog.create({
      component: TextToCopyDialog,
      componentProps: {
        textToCopy: textToCopy,
        title: "Copy Text",
        button: copyBtn
      }
    })
  }

  static goToInternalURL(internalURL: string): void {
    if (internalURL.startsWith("#")) {
      window.location.href = internalURL;
    } else {
      window.location.replace(`${window.location.origin}${window.location.pathname.replace(/\/[^/]*$/, '/')}` + internalURL);
    }
  }

  static getServerAddressWithERP(): string {
    let path = window.location.pathname;
    if (path.endsWith(".html"))
      path = path.substring(0, path.lastIndexOf('/'));
    return window.location.origin + path;
  }

  static gridSizeToPixels(size: string | undefined): number {
    if (!size)
      return 0;
    switch (size) {
      case GridSize.XXLarge:
        return 900;
      case GridSize.XLarge:
        return 700;
      case GridSize.Large:
        return 500;
      case GridSize.Medium:
        return 300;
      case GridSize.Small:
        return 200;
      case GridSize.VerySmall:
        return 150;
      case GridSize.Tiny:
        return 100;
      default:
        return 0;
    }

  }


  static elementOrParentHasClass(element: HTMLElement | null, className: string): boolean {
    while (element) {
      if (element.classList.contains(className)) {
        return true;
      }
      element = element.parentElement;
    }
    return false;
  }

  static scrollIntoView($el: Element) {
    if (!$el)
      return;
    $el.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  }

  static initWindowFunctions() {
    /*TODO:: Implement this */
    let w = window as any;
    w.previewAttachment = () => {};
    w.previewAttachmentMouseOut = () => {};
  }

  private static iconList: GoogleMaterialIcon[] = [];
  static RECENT_VISITS_KEY = "recent-visits";
  private static MIN_RECENT_VISITS = 3;
  private static MAX_RECENT_VISITS = 50;


  static async fetchIconList() {
    if (this.iconList.length)
      return this.iconList;
    const googleMaterialIcons = await NamaCommon.namaFetch("https://namasoft.com/bin/google-material-icons.json"
      , {method: "GET"})
    if (NamaCommon.isSuccess(googleMaterialIcons) && googleMaterialIcons.length)
      this.iconList = googleMaterialIcons;
    await this.fetchNamaIcons()
    return this.iconList;
  }

  private static getRecentVisits() {
    const visits = localStorage.getItem(this.RECENT_VISITS_KEY);
    if (visits) {
      const parsedValue = JSON.parse(visits).map(p => ({...p, date: new Date(p.date)}));
      const filtered = parsedValue.filter(v => v.count >= this.MIN_RECENT_VISITS).length < 10;
      if (filtered)
        this.MIN_RECENT_VISITS = 1;
      return parsedValue.filter(r => r.count >= this.MIN_RECENT_VISITS)
    } else
      return [];
  }

  static getSortedVisitsByDate() {
    const recentVisits = this.getRecentVisits();
    return recentVisits.sort((a, b) => b.date.getTime() - a.date.getTime());
  }

  static isSameDay(a: Date, b: Date) {
    return a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
  }

  static fetchRecentVisitsGroupedByDate(): RecentVisitGroupedByPeriod {
    const groups: RecentVisitGroupedByPeriod = {lastDay: [], lastDayBefore: [], olderDays: []};
    const sortedVisitsByDate = this.getSortedVisitsByDate();
    if (!sortedVisitsByDate.length)
      return groups;

    const lastDay = sortedVisitsByDate[0].date;
    const prevVisit = sortedVisitsByDate.find(v => !this.isSameDay(v.date, lastDay));
    const lastDayBefore = prevVisit ? prevVisit.date : null;

    for (const recentVisit of sortedVisitsByDate) {
      const date = new Date(recentVisit.date);
      if (this.isSameDay(date, lastDay))
        groups.lastDay.push(recentVisit);
      else if (lastDayBefore && this.isSameDay(date, lastDayBefore))
        groups.lastDayBefore.push(recentVisit);
      else
        groups.olderDays.push(recentVisit);
    }
    return groups;
  }

  static updateRecentVisits(route: RouteLocationNamedRaw, title?: string) {
    let recentVisits = this.getRecentVisits();
    const currentRoute = recentVisits.find(v => {
      if (ObjectChecker.areAllEqual(v.route.name, RouteNames.LIST_VIEW_ROUTE, route.name))
        return ObjectChecker.areEqual(v.route.query[PlaceTokens.PRM_ENTITY_TYPE], route.query[PlaceTokens.PRM_ENTITY_TYPE])
      else
        return ObjectChecker.areEqual(v.route, route)
    })
    const now = new Date();
    if (currentRoute) {
      currentRoute.count++;
      currentRoute.route = route;
      currentRoute.title = title;
      currentRoute.date = now;
    } else if (ObjectChecker.areNotEqual(route.name, RouteNames.WELCOME_ROUTE) && (ObjectChecker.areNotEqual(route.query[PlaceTokens.PRM_VIEW], ViewMode.NEW)))
      recentVisits.push({route, count: 1, title, date: now});
    if (recentVisits.length > this.MAX_RECENT_VISITS) {
      const sortByCountThenDate = (visits: RecentVisit[]) => visits.sort((a, b) => {
        if (a.count !== b.count)
          return a.count - b.count;
        return a.date.getTime() - b.date.getTime();
      })
      const sortedVisits = this.getSortedVisitsByDate();
      const lastDay = sortedVisits[0]?.date
      if (!lastDay)
        return;
      const visitsByLastDay = sortedVisits.filter(v => this.isSameDay(lastDay, v.date))
      if (visitsByLastDay.length > this.MAX_RECENT_VISITS) {
        sortByCountThenDate(visitsByLastDay);
        recentVisits = recentVisits.filter(v => v !== visitsByLastDay[0])
      } else {
        const prevLastDayVisits = recentVisits.filter(v => !this.isSameDay(lastDay, v.date))
        if (prevLastDayVisits.length)
          sortByCountThenDate(prevLastDayVisits);
        recentVisits = recentVisits.filter(v => v !== prevLastDayVisits[0]);
      }
    }
    this.saveToLocalStorage(this.RECENT_VISITS_KEY, recentVisits);
  }

  static deleteRecentVisit(route: RouteLocationNamedRaw) {
    let recentVisits = this.getRecentVisits();
    recentVisits = recentVisits.filter(v => ObjectChecker.areNotEqual(v.route, route))
    this.saveToLocalStorage(this.RECENT_VISITS_KEY, recentVisits);
  }

  static saveToLocalStorage(key: string, data: any) {
    localStorage.setItem(key, JSON.stringify(data));
  }

  private static async fetchNamaIcons() {
    this.iconList.unshift(...NamaIconList)
  }

  static async fetchFieldsHelpMessages(entityType: string) {
    const fieldIds = TopPanelSearchUtils.getUniqueFieldIds();
    const settings: NamaAxiosRequestConfig = {
      data: {
        entityType,
        fieldIds
      },
    };
    const data = await NamaCommon.namaFetch(NamaURLS.SearchForFieldsHelpMsgs, settings)
    if (!NamaCommon.isSuccess(data))
      return;
    useNamaFlagsStore().setFieldsHelpMessages(entityType, data.fieldsHelpMessages)
  }

  static convertEmToPx(emValue: string): number {
    const match = emValue.match(/^([+-]?\d*\.?\d+)em$/);
    if (!match) {
      return 0;
    }

    const emNumber = parseFloat(match[1]);
    const baseFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

    return Math.round(emNumber * baseFontSize);
  }
}

export interface GoogleMaterialIcon {
  name: string;
  version?: number;
  popularity?: number;
  codepoint?: number;
  unsupported_families?: string[];
  categories?: string[];
  tags?: string[];
  sizes_px?: number[];
}
